import { Axis, BoundingInfo, Quaternion, Vector3 } from '@babylonjs/core';
import { Injectable, Zed } from '@modules/core';
import { getXZCoordinateHash } from '@modules/standard';
import { World } from 'miniplex';

import {
  get2DFeature,
  get3DFeature,
  getDanji,
  getDanjiDong,
  getDanjiDongHo,
  getDecal,
  getEnviromentMaskURL,
  getItemIds,
  postItems,
} from './API';
import {
  Danji2DFacilityData,
  Danji2DFacilityExtendData,
  Danji3DFacilityData,
  Danji3DFacilityExtendData,
  DanjiData,
  DanjiDecalData,
  DanjiDongHoData,
  DanjiHoData,
  DanjiRenderData,
  DongData,
  DongExtendData,
  DongRenderData,
  ItemData,
  ItemHoRenderData,
} from './APIResponse';
import { RaycastSystem } from './ecs/system/RaycastSystem';
import { TOURDANJI_MODULE_OPTIONS_TOKEN, TourDanjiOptions } from './TourDanjiModule';
import { Base64, checkApproximaterly, floatArrayToVector3 } from './utiles/Utils';

export type DanjiEntity = {
  DanjiId?: string;
  DanjiData?: DanjiData;
};

type Entity = DanjiEntity;

/**
 * 설명 추가 필요
 *
 * @group System
 * @category System
 */
export class TourDanjiFetcher implements Injectable {
  static readonly INJECTABLE_TOKEN = Symbol.for('TourDanjiFetcher');
  // 지면에서 기본 높이만큼 띄운다 (1.0m)
  static readonly FLOOR_HEIGHT_OFFSET = 1.0;

  private readonly needTagFacilities = [
    '출입구',
    '지하주차장입구',
    '놀이터',
    '분리수거장',
    '운동시설',
    '상가',
    '보육시설',
    '정류장',
  ];
  private readonly polygonType = 'POLYGON';

  //------------------------------------------------------------------------------------
  // #### System Base ####
  //------------------------------------------------------------------------------------
  private readonly world;

  private readonly danjiOptions;

  private readonly raycastSystem;

  constructor({
    [Zed.WORLD_TOKEN]: world,
    [TOURDANJI_MODULE_OPTIONS_TOKEN]: danjiOptions,
    [RaycastSystem.INJECTABLE_TOKEN]: raycastSystem,
  }: {
    [Zed.WORLD_TOKEN]: World<Entity>;
    [TOURDANJI_MODULE_OPTIONS_TOKEN]: TourDanjiOptions;
    [RaycastSystem.INJECTABLE_TOKEN]: RaycastSystem;
  }) {
    this.world = world;

    this.danjiOptions = danjiOptions;

    this.raycastSystem = raycastSystem;
  }

  dispose() {
    this._fetchedDecalData = undefined;
    this._enviromentMaskURL = undefined;
    this._fetchedDanjiData = undefined;
    this._fetchedItemIds = undefined;
    this._fetchedDongData = undefined;
    this._fetchedItemData = undefined;
    this._fetchedRenderData = undefined;
    this._fetchedRaw2dFacilityData = undefined;
    this._fetchedRaw3dFacilityData = undefined;
    this._facility2DDatas = undefined;
    this._facility3DDatas = undefined;    
    this._fetchedUnitDataMap = undefined;
  }

  //--------------------------------------------
  private _fetchedDecalData: DanjiDecalData | undefined;
  public get decalData(): Readonly<DanjiDecalData> | undefined {
    return this._fetchedDecalData;
  }
  private _enviromentMaskURL: string | undefined;
  public get enviromentMaskURL(): Readonly<string> | undefined {
    return this._enviromentMaskURL;
  }
  public async fetchDecal(danjiId: string) {
    try {
      if (!this.danjiOptions.disableDecal) {
        this._enviromentMaskURL = getEnviromentMaskURL(danjiId, 4096);
        this._fetchedDecalData = await getDecal(danjiId);
      }
    } catch (error) {
      console.error(error);
    }
  }

  //--------------------------------------------
  private _fetchedDanjiData: DanjiData | undefined;
  public get danjiData(): Readonly<DanjiData> | undefined {
    return this._fetchedDanjiData;
  }
  public async fetchDanji(danjiId: string) {
    this._fetchedDanjiData = await getDanji(danjiId);    

    this.world.with('DanjiId', 'DanjiData').clear();
    this.world.add({
      DanjiId: danjiId,
      DanjiData: this._fetchedDanjiData,
    });
  }

  //--------------------------------------------
  private _fetchedItemIds: number[] | undefined;
  public get itemIds(): Readonly<number[]> {
    return this._fetchedItemIds ?? [];
  }
  public async fetchItemIds(danjiId: string) {
    const itemIds = await getItemIds(danjiId);
    this._fetchedItemIds = itemIds;
    return itemIds;
  }

  private _fetchedDongData: DongExtendData[] | undefined;
  public get dongs(): Readonly<DongExtendData[]> | undefined {
    return this._fetchedDongData;
  }
  private _fetchedItemData: ItemData[] | undefined;
  public get items(): Readonly<ItemData[]> | undefined {
    return this._fetchedItemData;
  }

  public async fetchDong(danjiId: string, renderingItemDongOnly: boolean = false, exceptRoofTop: boolean = false) {
    if (!this._fetchedDanjiData) return;

    if (renderingItemDongOnly) {
      const items = await this.getItems([...this.itemIds]);

      this._fetchedDanjiData.dongs = this._fetchedDanjiData.dongs.filter(
        (dong) => !!items.find((item) => item.areaBuildingId === dong.areaBuildingId)
      );
      const dongs = await this.getDanjiDong(danjiId, this._fetchedDanjiData);

      this._fetchedItemData = items;
      this._fetchedDongData = dongs;
      this._fetchedUnitDataMap = new Map(dongs.flatMap((dong) => dong.building.hos.map((ho) => [ho.meta.ho_id, ho])));
    } else {
      const [dongs, items] = await Promise.all([
        this.getDanjiDong(danjiId, this._fetchedDanjiData, exceptRoofTop),
        this.getItems([...this.itemIds]),
      ]);
      this._fetchedDongData = dongs;
      this._fetchedItemData = items;
      this._fetchedUnitDataMap = new Map(dongs.flatMap((dong) => dong.building.hos.map((ho) => [ho.meta.ho_id, ho])));
    }

    if (this._fetchedDongData.length > 0) await this.createRenderData();
  }
  private async getDanjiDong(danjiId: string, danji: DanjiData, exceptRoofTop: boolean = false) {
    const dongDataFromBuildingId = new Map<number, DongData>();

    danji.dongs.forEach((dong) => {
      dongDataFromBuildingId.set(dong.areaBuildingId, dong);
    });

    const danjiDongs = await getDanjiDong(danjiId);
    const danjiDongHos = await Promise.all(
      danjiDongs.map((danjiDong) => this.getDanjiDongHo(danjiId, danjiDong.meta.bd_id))
    );

    const dongs: DongExtendData[] = danjiDongs.flatMap((danjiDong, index) => {
      const danjiDongHo = danjiDongHos[index];

      const roomtypes = danjiDong.roomtypes.map((roomType) => ({
        ...roomType,
        coordinatesArray: roomType.coordinatesBase64s.map((x) => Base64.decodeXZY(x)),
      }));

      const roofTopCoordinates = !exceptRoofTop
        ? danjiDong.roofTops
          ? danjiDong.roofTops.coordinatesBase64s.flatMap((x) => Base64.decodeXZY(x))
          : []
        : [];

      const dongData = dongDataFromBuildingId.get(danjiDong.meta.bd_id);

      if (!dongData) {
        // console.log(`DongData not found for buildingId: ${danjiDong.meta.bd_id}`);
        return [];
      }

      return {
        ...dongData,
        ...danjiDong,
        ...danjiDongHo,
        roomtypes,
        roofTopCoordinates,
      } as DongExtendData;
    });

    return dongs;
  }
  private async getDanjiDongHo(danjiId: string, buildingId: number) {
    const data = await getDanjiDongHo(danjiId, buildingId);
    const transformedData = {
      ...data,
      transforms: data.transforms.map((transform) => {
        const keyArr = transform.Key ? transform.Key.split('_') : [];
        const 호 = keyArr.length > 1 ? `${keyArr[1]!.replace('호', '')}호` : undefined;
        const 복층인덱스 = keyArr.length > 2 ? Number.parseInt(keyArr[2]!) : undefined;
        return {
          ...transform,
          호,
          복층인덱스,
        };
      }),
    };

    return transformedData as DanjiDongHoData;
  }
  private async getItems(itemIds: number[]) {
    const items = await postItems(itemIds);
    return items;
  }

  private _fetchedRenderData: DanjiRenderData | undefined;
  public get renderData(): Readonly<DanjiRenderData> | undefined {
    return this._fetchedRenderData;
  }
  private async createRenderData() {
    if (!this._fetchedDanjiData || !this._fetchedDongData) return;

    const dongRenderDatas = this.createDongsModel(this._fetchedDongData);
    const min = dongRenderDatas.map((x) => x.boundingInfo.minimum).reduce((x, y) => Vector3.Minimize(x, y));
    const max = dongRenderDatas.map((x) => x.boundingInfo.maximum).reduce((x, y) => Vector3.Maximize(x, y));
    const boundingInfo = new BoundingInfo(min, max);
    const itemRenderDatas = this.createItemModel();

    this._fetchedRenderData = {
      dongRenderDatas,
      boundingInfo,
      itemRenderDatas,
    };
  }
  private createDongsModel(dongs: DongExtendData[]): DongRenderData[] {
    return dongs.map((dong) => this.createDongModel(dong));
  }
  private createDongModel(dong: DongExtendData): DongRenderData {
    //------------------------------------
    // coordinatesArray를 vertices로 변환하며 min/max 값을 계산해 BoundingInfo 생성
    const vertices: number[] = [];
    const min = new Vector3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY);
    const max = new Vector3(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY);

    dong.roomtypes.forEach((roomtype) => {
      roomtype.coordinatesArray.forEach((coordinates) => {
        for (let i = 0; i < coordinates.length; i += 3) {
          const x = coordinates[i]!;
          const y = coordinates[i + 1]!;
          const z = coordinates[i + 2]!;
          vertices.push(x, y, z);

          min.x = Math.min(min.x, x);
          min.y = Math.min(min.y, y);
          min.z = Math.min(min.z, z);
          max.x = Math.max(max.x, x);
          max.y = Math.max(max.y, y);
          max.z = Math.max(max.z, z);
        }
      });
    });
    const boundingInfo = new BoundingInfo(min, max);

    //------------------------------------
    const position = new Vector3();
    const minY = Math.min(...vertices.filter((_x, i) => i % 3 === 1));
    const firstHo = dong.building.hos[0];
    const floorHeight = firstHo ? firstHo.meta.높이 / firstHo.meta.층갯수 : 3;

    // position의 y 좌표를 landPosition의 y 좌표로 설정합니다.
    position.y = this.calcFitToLand(dong, minY);

    return {
      roomtypes: dong.roomtypes,
      roofTopCoordinates: dong.roofTopCoordinates,
      dongName: dong.name,
      position: position,
      floors: dong.층수,
      boundingInfo,
      areaBuildingId: dong.areaBuildingId,
      height: minY,
      floorHeight,
    };
  }
  //--------------------------------------------
  private _fetchedRaw2dFacilityData?: Danji2DFacilityData[] | undefined;
  public get raw2dFacilityData(): Readonly<Danji2DFacilityData[]> | undefined {
    return this._fetchedRaw2dFacilityData;
  }
  private _facility2DDatas?: Danji2DFacilityExtendData[] | undefined;
  public get facility2DDatas(): Readonly<Danji2DFacilityExtendData[]> | undefined {
    return this._facility2DDatas;
  }

  private _fetchedRaw3dFacilityData?: Danji3DFacilityData[] | undefined;
  public get raw3dFacilityData(): Readonly<Danji3DFacilityData[]> | undefined {
    return this._fetchedRaw3dFacilityData;
  }
  private _facility3DDatas?: Danji3DFacilityExtendData[] | undefined;
  public get facility3DDatas(): Readonly<Danji3DFacilityExtendData[]> | undefined {
    return this._facility3DDatas;
  }
  public async fetchRaw2dFacilityData(danjiId: string) {
    try {
      this._fetchedRaw2dFacilityData = await get2DFeature(danjiId);
    } catch (ex) {
      console.warn(`Failed to fetch 2D Facilities with error: ${ex}`);
      this._fetchedRaw2dFacilityData = [];
    }
  }
  public async fetchRaw3dFacilityData(danjiId: string) {
    try {
      this._fetchedRaw3dFacilityData = await get3DFeature(danjiId);
    } catch (ex) {
      console.warn(`Failed to fetch 3D Facilities with error: ${ex}`);
      this._fetchedRaw3dFacilityData = [];
    }
  }
  public convertFacilityData() {
    this.convert2DFacilityData();
    this.convert3DFacilityData();
  }

  public convert2DFacilityData() {
    this._facility2DDatas = this._fetchedRaw2dFacilityData?.map((x) => this.adjust2DFacilityData(x));
  }

  public convert3DFacilityData() {
    this._facility3DDatas = this._fetchedRaw3dFacilityData?.map((x) => this.adjust3DFacilityData(x));
  }

  private adjust2DFacilityData(data: Danji2DFacilityData) {
    const isPolygon = data.coordinatesType === this.polygonType;
    const hasTag = this.needTagFacilities.includes(data.name);
    const coordinates = this.getCoordinates(data.coordinatesBase64, isPolygon);
    const positions = this.adjustFloorCoordinate(coordinates);
    const scaleInPlaces = positions.map(() => 0.1 + Math.random() * 0.05);
    const rotations = positions.map(() => Quaternion.RotationAxis(Axis.Y, Math.random() * Math.PI * 2));
    const directions = positions.map(() => data.meta?.direction);
    const roadWidths = positions.map(() => data.meta?.도로폭);

    const holeCoordinates = this.getCoordinates(data.holeCoordinatesBase64, isPolygon);

    return {
      ...data,
      hasTag,
      positions,
      rotations,
      scaleInPlaces,
      coordinates,
      holeCoordinates,
      directions,
      roadWidths,
    } as Danji2DFacilityExtendData;
  }
  private adjust3DFacilityData(data: Danji3DFacilityData) {
    const isPolygon = data.coordinatesType === 'POLYGON';
    const hasTag = this.needTagFacilities.includes(data.name);
    const coordinates = this.convert3DCoordinates(this.getCoordinates(data.coordinatesBase64, isPolygon));

    const boundaryCoordinates = this.convert3DCoordinates(
      this.getCoordinates(data.boundaryCoordinatesBase64, isPolygon)
    );
    const roofTopCoordinates = this.convert3DCoordinates(this.getCoordinates(data.roofTopCoordinatesBase64, isPolygon));

    return {
      ...data,
      hasTag,
      coordinates,
      boundaryCoordinates,
      roofTopCoordinates,
    } as Danji3DFacilityExtendData;
  }

  private convert3DCoordinates(coordinates: number[]) {
    const arr = Array<Vector3>();

    for (let i = 0; i < coordinates.length; i += 3) {
      const x = coordinates[i + 0];
      const z = coordinates[i + 1];
      const y = coordinates[i + 2];
      arr.push(new Vector3(x, y, z));
    }

    return arr;
  }
  private getCoordinates(coordinatesBase64: string, emptyIfDirty: boolean) {
    const coordinates = Base64.decode(coordinatesBase64);
    const sanitizilized = this.sanitizeCoords(coordinates);

    if (emptyIfDirty && coordinates.length !== sanitizilized.length) {
      return [];
    }

    return sanitizilized;
  }
  private sanitizeCoords(coordinates?: number[]) {
    if (!coordinates) return [];
    if (coordinates.length === 0) return coordinates;

    const sanitizilzed = coordinates.filter((x) => Number.isNaN(x) || Number.isFinite(x));
    const isClean = sanitizilzed.length === coordinates.length;
    if (!isClean) {
      console.error('Coords are not clean');
    }

    return sanitizilzed;
  }
  private adjustFloorCoordinate(coordinates: number[]) {
    const arr: Vector3[] = [];
    for (let i = 0; i < coordinates.length; i += 2) {
      const hit = this.raycastSystem.pickWithRayByLand(coordinates[i + 0]!, coordinates[i + 1]!);

      if (hit?.hit) {
        const point = hit.pickedPoint!;
        arr.push(point.clone());
      } else {
        arr.push(Vector3.Zero());
      }
    }

    return arr;
  }
  public adjustFloorCoordinates(coordinates: Vector3[], floor: number, zOrder?: number) {
    const minY = Math.min(...coordinates.map((x) => x.y));
    const adjustedCoords: Vector3[] = [];
    for (let i = 0; i < coordinates.length; i += 3) {
      var first = coordinates[i]!;
      var second = coordinates[i + 1]!;
      var third = coordinates[i + 2]!;

      if (first.y != minY || second.y != minY || third.y != minY) {
        adjustedCoords.push(first.clone(), second.clone(), third.clone());
      }
    }

    let isOnLand = true;
    for (const coord of adjustedCoords) {
      if (coord.y == minY) {
        const hit = this.raycastSystem.pickWithRayByLand(coord.x, coord.z, zOrder);
        if (hit?.hit) {
          isOnLand = false;
          break;
        }
      }
    }

    const floorCoordinateHitPoints = [];
    for (let i = 0; i < adjustedCoords.length; ++i) {
      const coord = adjustedCoords[i]!;
      if (coord.y == minY) {
        if (isOnLand) {
          const hit = this.raycastSystem.pickWithRayByGround(coord.x, coord.z);
          if (hit?.pickedPoint) floorCoordinateHitPoints.push(hit.pickedPoint);
          else floorCoordinateHitPoints.push(Vector3.ZeroReadOnly);
        } else {
          const hit = this.raycastSystem.pickWithRayByLand(coord.x, coord.z, zOrder);
          if (hit?.pickedPoint) {
            floorCoordinateHitPoints.push(hit.pickedPoint);
          } else floorCoordinateHitPoints.push(Vector3.ZeroReadOnly);
        }
      } else {
        floorCoordinateHitPoints.push(Vector3.ZeroReadOnly);
      }
    }

    const maxHitY = Math.max(...floorCoordinateHitPoints.map((h) => h.y));
    for (let i = 0; i < adjustedCoords.length; ++i) {
      const coord = adjustedCoords[i]!;
      if (coord.y == minY) {
        adjustedCoords[i] = new Vector3(coord.x, floorCoordinateHitPoints[i]!.y, coord.z);
      } else {
        if (floor > 0) adjustedCoords[i]!.y += maxHitY;
        else adjustedCoords[i] = new Vector3(adjustedCoords[i]!.x, maxHitY, adjustedCoords[i]!.z);
      }
    }

    return adjustedCoords;
  }

  private createItemModel(): ItemHoRenderData[] {
    const datas = Array<ItemHoRenderData>();
    const dongs = this._fetchedDongData;
    const items = this._fetchedItemData;

    if (!items || !dongs) {
      console.log('No data was found in the fetched results.');
      return datas;
    }

    const validItems = items.filter((t) => dongs.some((dong) => t.areaBuildingId === dong.meta.bd_id));
    if (validItems.length === 0) {
      return datas;
    }

    const hos = dongs.flatMap((dong) => dong.building.hos);
    const hosByHoId = new Map<string, DanjiHoData>();
    for (const ho of hos) {
      hosByHoId.set(ho.meta.ho_id.toString(), ho);
    }

    for (const item of validItems) {
      const key = item.areaHoId.toString();
      const ho = hosByHoId.get(key)!;
      if (!ho) {
        console.log('Unable to find a hoId.');
        continue;
      }

      const coordinates = Base64.decode(ho.coordinatesBase64);

      datas.push({
        ho,
        coordinates,
        ...item,
      });
    }

    return datas;
  }

  /**
   * This method calculates the fitting of a building to the land.
   *
   * @param {DongExtendData} dong - The extended data of the Dong (neighborhood) where the building is located.
   * @param {number} height - The height of the building.
   *
   * @returns {number} - The y-coordinate of the land position that has the highest y-value among the land points.
   *
   * The method works as follows:
   * 1. It retrieves the ground height from the dong metadata, or if not available, from the building metadata.
   * 2. It decodes the building's coordinates from Base64 and converts them to Vector3 format.
   * 3. It filters out duplicate vectors.
   * 4. It finds the vertices at the target height and those between the vertices at the building's height.
   * 5. It combines these vertices, removes duplicates, and finds the edge vectors.
   * 6. It uses the raycast system to get the land points from the edge vectors.
   * 7. It finds the land point with the highest y-value (i.e., the highest point on the land).
   *
   * @remarks Originally from DanjiDongRenderer.cs `GetBoundPoints` `FitToLand`
   */
  private calcFitToLand(dong: DongExtendData, height: number) {
    const buildingData = dong.building;
    const targetHeight = dong.meta.지면높이 ?? buildingData.meta.지면높이;
    let vArray = new Array<Vector3>();
    buildingData.hos.forEach((t: DanjiHoData) => {
      const stof = Base64.decodeXZY(t.coordinatesBase64);
      const ftov = floatArrayToVector3(stof);
      vArray.push(...ftov);
    });
    vArray = vArray.filter((v, i) => vArray.indexOf(v) === i);
    const boundsAtVertices = vArray.filter((t) => checkApproximaterly(t.y, height + targetHeight));
    const boundsBetweenVertices = vArray
      .filter((t) => checkApproximaterly(t.y, height))
      .map((t) => new Vector3(Math.round(t.x), t.y + targetHeight, Math.round(t.z)));
    const boundPoint = boundsAtVertices.concat(boundsBetweenVertices);
    const groups = new Map<string, Vector3>();
    boundPoint.forEach((p) => {
      const key = getXZCoordinateHash(p);
      if (!groups.has(key)) {
        const pickingInfo = this.raycastSystem.pickWithRayByLand(p.x, p.z, 0);
        if (pickingInfo?.pickedPoint) {
          groups.set(key, pickingInfo.pickedPoint);
        }
      }
    });
    return Math.max(...Array.from(groups.values()).map((v) => v.y - targetHeight)) + TourDanjiFetcher.FLOOR_HEIGHT_OFFSET;
  }

  public getBuildingByHoId(hoId: number) {
    return this.dongs?.find((v) => v.building.hos.some((ho) => ho.meta.ho_id === hoId));
  }
  
  public getItem(itemId: number) {
    return this.items?.find((v) => v.id === itemId);
  }

  private _fetchedUnitDataMap: Map<number, DanjiHoData> | undefined;
  public getUnitData(unitId: number) {
    return this._fetchedUnitDataMap?.get(unitId);
  }
}
